import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Globe2, X, Check } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { WORLD_REGIONS, getRegion, type Continent } from "../../utils/regions";
import { regionLabel } from "../../utils/regionLabel";
import { passErrorMessage, timeLeftParts, useGlobalPass, usePassView } from "../../utils/globalPass";

const CONTINENT_KEY: Readonly<Record<Continent, string>> = {
  Asia: "asia",
  "Middle East": "middleEast",
  Europe: "europe",
  Americas: "americas",
};


const CSS = `
.gp-root{--gp-gold:#f5d99a;--gp-accent:var(--astria-accent-primary,#a78bff);--gp-t1:var(--astria-text-primary,#f7f3fa);--gp-t2:var(--astria-text-secondary,#c8bfd1);--gp-t3:rgba(247,243,250,.5);--gp-card:radial-gradient(120% 140% at 0% 0%,rgba(167,139,255,.22),transparent 55%),var(--astria-surface,#1a131f);--gp-modal:var(--astria-surface,#1a131f);--gp-border:rgba(245,217,154,.35);--gp-shadow:var(--astria-shadow-card,0 10px 40px -12px rgba(167,139,255,.45));--gp-btn:linear-gradient(135deg,#f5d99a,#e0b866);--gp-btn-t:#1a1233;--gp-chip:rgba(245,217,154,.15);--gp-tile:rgba(255,255,255,.05);--gp-tile-b:rgba(255,255,255,.1);--gp-track:rgba(255,255,255,.1);--gp-core:radial-gradient(circle at 30% 25%,#3a2c7a,#140f33 70%);--gp-shine:rgba(255,255,255,.08);--gp-scrim:rgba(0,0,0,.6)}
html[data-theme="light"] .gp-root{--gp-gold:#b8861f;--gp-t3:rgba(26,26,31,.5);--gp-card:radial-gradient(120% 140% at 0% 0%,rgba(122,92,255,.14),transparent 55%),var(--astria-surface,#F2F2F7);--gp-modal:#fff;--gp-border:rgba(184,134,31,.4);--gp-shadow:var(--astria-shadow-card,0 2px 8px rgba(26,26,31,.06));--gp-btn:linear-gradient(135deg,#7a5cff,#5b3fe0);--gp-btn-t:#fff;--gp-chip:rgba(184,134,31,.12);--gp-tile:rgba(26,26,31,.04);--gp-tile-b:rgba(26,26,31,.1);--gp-track:rgba(26,26,31,.1);--gp-core:radial-gradient(circle at 30% 25%,#efe8ff,#d6c9ff 70%);--gp-shine:rgba(255,255,255,.55);--gp-scrim:rgba(26,26,31,.35)}
.gp-t1{color:var(--gp-t1)}.gp-t2{color:var(--gp-t2)}.gp-t3{color:var(--gp-t3)}.gp-gold{color:var(--gp-gold)}
.gp-btn{background:var(--gp-btn);color:var(--gp-btn-t)}
@keyframes gp-spin { to { transform: rotate(360deg); } }
@keyframes gp-float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }
@keyframes gp-shimmer { 0% { transform: translateX(-120%); } 100% { transform: translateX(220%); } }
@media (prefers-reduced-motion: reduce) { .gp-anim { animation: none !important; } }
`;

const Orb: React.FC<{ size?: number }> = ({ size = 64 }) => (
  <div className="relative flex-shrink-0" style={{ width: size, height: size }} aria-hidden="true">
    <div
      className="gp-anim absolute inset-0 rounded-full"
      style={{
        background: "conic-gradient(from 0deg, var(--gp-gold), var(--gp-accent), transparent 60%, var(--gp-gold))",
        animation: "gp-spin 9s linear infinite",
        opacity: 0.85,
      }}
    />
    <div
      className="absolute rounded-full flex items-center justify-center gp-anim"
      style={{
        inset: 3,
        background: "var(--gp-core)",
        animation: "gp-float 4s ease-in-out infinite",
      }}
    >
      <Globe2 size={size * 0.46} style={{ color: "var(--gp-gold)" }} strokeWidth={1.5} />
    </div>
  </div>
);

const Shell: React.FC<{ children: React.ReactNode; label: string }> = ({ children, label }) => (
  <div
    role="region"
    aria-label={label}
    className="relative mx-auto w-full max-w-[560px] overflow-hidden rounded-3xl p-5"
    style={{ background: "var(--gp-card)", border: "1px solid var(--gp-border)", boxShadow: "var(--gp-shadow)" }}
  >
    <div
      className="gp-anim pointer-events-none absolute inset-y-0 w-1/3"
      style={{
        background: "linear-gradient(100deg, transparent, var(--gp-shine), transparent)",
        animation: "gp-shimmer 6s ease-in-out infinite",
      }}
    />
    {children}
  </div>
);


/** Modal wrapper shared by the picker and the "ended" popup. */
const Modal: React.FC<{ onClose: () => void; children: React.ReactNode; label: string; closeLabel: string }> = ({
  onClose,
  children,
  label,
  closeLabel,
}) => (
  <div
    className="fixed inset-0 z-[1000] flex items-end justify-center p-4 backdrop-blur-sm sm:items-center"
    style={{ background: "var(--gp-scrim)" }}
    onClick={onClose}
    role="dialog"
    aria-modal="true"
    aria-label={label}
  >
    <div
      className="relative w-full max-w-md rounded-3xl p-6 text-center"
      style={{ background: "var(--gp-modal)", border: "1px solid var(--gp-border)", boxShadow: "var(--gp-shadow)" }}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label={closeLabel}
        className="absolute right-4 top-4 gp-t3 hover:opacity-80"
      >
        <X size={18} />
      </button>
      {children}
    </div>
  </div>
);

/**
 * Global Pass card on "/" (Today) for Free (1 pass/month) and Pro (2 passes/month).
 * Global users never see it. Four looks, one slot:
 * ready → active → ended (popup) → ready again if a pass is left, else hidden.
 */
const GlobalPassCard: React.FC<{ className?: string }> = ({ className = "" }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, updateCurrentUser } = useAuth();
  const pass = useGlobalPass((user as any)?._id || (user as any)?.id || user?.email);
  const view = usePassView();
  const [picking, setPicking] = useState(false);
  const [chosen, setChosen] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const status = pass.state?.status;
  const remaining = pass.state?.remaining ?? 0;
  const limit = pass.state?.limit ?? 0;

  // Countries the user already has are not offered (the server refuses them too).
  const homeRegion = view.homeRegion;
  const unlocked = user?.unlockedRegions;
  const options = useMemo(
    () => WORLD_REGIONS.filter((r) => r.value !== homeRegion && !unlocked?.includes(r.value)),
    [homeRegion, unlocked],
  );

  useEffect(() => {
    if (status === "ended") document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [status]);

  // Tell the rest of the app (sidebar category filter) which region the pass opens.
  // The server still decides what content is allowed; this only changes what is listed.
  const passRegion = status === "active" ? pass.state?.region ?? null : null;
  const passExpiresAt = status === "active" ? pass.state?.expiresAt ?? null : null;
  useEffect(() => {
    if (!pass.loaded) return;
    if ((user as any)?.globalPassRegion !== passRegion) {
      updateCurrentUser({ globalPassRegion: passRegion, globalPassExpiresAt: passExpiresAt } as any);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pass.loaded, passRegion, passExpiresAt]);

  const startPass = async () => {
    if (!chosen || busy) return;
    setBusy(true);
    setError("");
    try {
      await pass.activate(chosen);
      setPicking(false);
      setChosen(null);
    } catch (err) {
      setError(passErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  // The server says eligible: false for Astria Global (every country already open).
  if (!user || !pass.state?.eligible) return null;

  // getRegion resolves aliases ("Thailand" stored as user.region) to the key the locale files use.
  const countryName = (value: string | null) => regionLabel(t, getRegion(value)?.value ?? value ?? "");

  return (
    <div className={`gp-root ${className}`}>
      <style>{CSS}</style>

      {status === "ready" && (
        <Shell label={t("globalPass.name")}>
          <div className="relative flex items-center gap-4">
            <Orb />
            <div className="min-w-0 flex-1">
              <span
                className="inline-block rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest"
                style={{ background: "var(--gp-chip)", color: "var(--gp-gold)" }}
              >
                {limit > 1 ? t("globalPass.chipLeft", { remaining, limit }) : t("globalPass.chipThisMonth")} ·{" "}
                {t("globalPass.chipHours")}
              </span>
              <h3 className="mt-1.5 text-lg font-semibold gp-t1">{t("globalPass.readyTitle")}</h3>
            </div>
          </div>
          <p className="relative mt-3 text-sm leading-relaxed gp-t2">
            {t("globalPass.readyText")}
          </p>
          <div className="relative mt-4 flex items-center gap-3">
            <button
              type="button"
              onClick={() => setPicking(true)}
              className="flex-1 rounded-full px-5 py-3 text-sm font-semibold gp-btn"
              
            >
              {t("globalPass.use")}
            </button>
            <button type="button" onClick={pass.dismiss} className="px-3 py-3 text-sm gp-t3 hover:opacity-80">
              {t("globalPass.later")}
            </button>
          </div>
        </Shell>
      )}

      {status === "dismissed" && (
        <button
          type="button"
          onClick={() => setPicking(true)}
          className="mx-auto flex w-full max-w-[560px] items-center gap-3 rounded-full px-4 py-2.5 text-left"
          style={{ background: "var(--gp-chip)", border: "1px solid var(--gp-border)" }}
        >
          <Globe2 size={18} className="gp-gold" />
          <span className="flex-1 text-sm gp-t1">
            {t("globalPass.dismissedTitle")}
            {limit > 1 ? ` · ${t("globalPass.dismissedLeft", { remaining })}` : ""}
          </span>
          <span className="text-xs font-semibold gp-gold" >
            {t("globalPass.useShort")}
          </span>
        </button>
      )}

      {status === "active" && (
        <Shell label={t("globalPass.active")}>
          <div className="relative flex items-center gap-4">
            <Orb size={56} />
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-semibold uppercase tracking-widest gp-gold" >
                {t("globalPass.active")}
              </p>
              <h3 className="mt-0.5 truncate text-lg font-semibold gp-t1">
                {t("globalPass.collection", { region: countryName(pass.state.region) })}
              </h3>
              <p className="text-xs gp-t3">{t("globalPass.timeLeft", timeLeftParts(pass.msLeft))}</p>
            </div>
            {view.exploring ? (
              <button
                type="button"
                onClick={() => {
                  view.goHome();
                  navigate("/home");
                }}
                className="flex-shrink-0 rounded-full px-4 py-2.5 text-xs font-semibold gp-btn"
              >
                {t("globalPass.backTo", { region: countryName(view.homeRegion) })}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  view.explore();
                  navigate("/home");
                }}
                className="flex-shrink-0 rounded-full px-4 py-2.5 text-xs font-semibold gp-btn"
              >
                {t("globalPass.explore")}
              </button>
            )}
          </div>
          <div className="relative mt-4 h-1.5 overflow-hidden rounded-full" style={{ background: "var(--gp-track)" }}>
            <div
              className="h-full rounded-full"
              style={{ width: `${(pass.msLeft / 86_400_000) * 100}%`, background: "linear-gradient(90deg, var(--gp-accent), var(--gp-gold))" }}
            />
          </div>
        </Shell>
      )}

      {picking && (
        <Modal onClose={() => setPicking(false)} label={t("globalPass.chooseLabel")} closeLabel={t("globalPass.close")}>
          <h3 className="text-lg font-semibold gp-t1">{t("globalPass.chooseTitle")}</h3>
          <p className="mt-1 text-sm gp-t3">{t("globalPass.chooseText")}</p>
          <div className="mt-4 grid max-h-[46vh] grid-cols-2 gap-2 overflow-y-auto pr-1 text-left">
            {options.map((r) => {
              const on = chosen === r.value;
              return (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setChosen(r.value)}
                  className="flex items-center justify-between rounded-2xl px-3 py-2.5"
                  style={{
                    background: on ? "var(--gp-chip)" : "var(--gp-tile)",
                    border: `1px solid ${on ? "var(--gp-gold)" : "var(--gp-tile-b)"}`,
                  }}
                >
                  <span>
                    <span className="block text-sm font-medium gp-t1">{regionLabel(t, r.value)}</span>
                    <span className="block text-[11px] gp-t3">{t(`globalPass.continent.${CONTINENT_KEY[r.continent]}`)}</span>
                  </span>
                  {on && <Check size={16}  />}
                </button>
              );
            })}
          </div>
          {error && (
            <p role="alert" className="mt-3 text-sm gp-t2">
              {error}
            </p>
          )}
          <button
            type="button"
            disabled={!chosen || busy}
            onClick={startPass}
            className="mt-4 w-full rounded-full px-5 py-3 text-sm font-semibold disabled:opacity-40 gp-btn"
            
          >
            {t("globalPass.use")}
          </button>
        </Modal>
      )}

      {status === "ended" && (
        <Modal onClose={pass.markEndedSeen} label={t("globalPass.endedLabel")} closeLabel={t("globalPass.close")}>
          <div className="flex justify-center">
            <Orb size={72} />
          </div>
          <h3 className="mt-4 text-xl font-semibold gp-t1">{t("globalPass.endedTitle")}</h3>
          <p className="mt-1 text-sm font-medium gp-gold" >
            {t("globalPass.endedTagline")}
          </p>
          <p className="mt-2 text-sm leading-relaxed gp-t2">
            {t("globalPass.endedText")}
          </p>
          {remaining > 0 && (
            <p className="mt-2 text-sm font-medium gp-t1">
              {t(remaining === 1 ? "globalPass.remainingOne" : "globalPass.remainingOther", { remaining })}
            </p>
          )}
          <button
            type="button"
            onClick={() => {
              pass.markEndedSeen();
              navigate("/account/plans-billing");
            }}
            className="mt-5 w-full rounded-full px-5 py-3 text-sm font-semibold gp-btn"
            
          >
            {t("globalPass.endedCta")}
          </button>
          <button type="button" onClick={pass.markEndedSeen} className="mt-2 w-full py-2 text-sm gp-t3 hover:opacity-80">
            {t("globalPass.notNow")}
          </button>
        </Modal>
      )}
    </div>
  );
};

export default GlobalPassCard;
